import { useState, useRef, useEffect } from "react";
import { ProductScheme, UserScheme, Volunteer } from "client-v2";
import { useDispatch, useSelector } from "react-redux";
import { actionsCreators, State } from "../../redux";
import { bindActionCreators } from "redux";
import styled from "styled-components";
import media from "styled-media-query";

import WizardContainer, {
	WizardContainerRef,
} from "../../components/container/WizardContainer";
import Success from "../../components/section/success/Success";
import PaymentMethod from "../../components/section/donation/PaymentMethod";
import ShippingLocationStep from "../../components/section/shop/ShippingLocation";
import PriceResume from "./PriceResume";
import SuccessRight from "./SuccessRight";
import { Modal, Text } from "@adoptaunabuelo/react-components";

const Footer = styled.div`
	display: flex;
	flex: 1;
	justify-content: center;
	flex-direction: column;
`;
const PackImage = styled.img`
	height: 416px;
	width: auto;
	margin-bottom: 32px;
	${media.lessThan("medium")`
		width: 100%;
		height: auto;
		margin: 0px;
	`}
`;

const Checkout = (props: Props) => {
	const dispatch = useDispatch();
	const { setShowError } = bindActionCreators(
		actionsCreators,
		dispatch
	);
	const isMobile: boolean = useSelector(
		(state: State) => state.screen.isMobile
	);
	const currentUser = useSelector((state: State) => state.user.currentUser);

	const wizard = useRef<WizardContainerRef>(null);

	const [shipping, setShipping] = useState<
		{ amount: number; currency: string } | undefined
	>(undefined);
	const [price, setPrice] = useState(0);
	const [shippingAddress, setShippingAddress] = useState<
		| {
				route: string;
				routeNumber?: string;
				routeInfo?: string;
				city?: string;
				zipCode: string;
				country: string;
		  }
		| undefined
	>(undefined);
	const [showResume, setShowResume] = useState(false);
	const [isSubscriptor, setIsSubscriptor] = useState(props.isSubscriptor);

	useEffect(() => {
		setIsSubscriptor(props.isSubscriptor);
		const tempCurrencyName = "EUR";
		const tempPrice = props.product.data
			? props.product.data[tempCurrencyName].price
			: 0;
		const tempAmbassadorPrice = props.isSubscriptor
			? props.product.data
				? props.product.data[tempCurrencyName].ambassadorPrice || 0
				: 0
			: 0;
		setPrice(props.isSubscriptor ? tempAmbassadorPrice : tempPrice);
	}, [props.isSubscriptor]);

	const onLogin = async (user: UserScheme) => {
		const isSubscriptor = await Volunteer.isSubscriptor(user.objectId);
		setIsSubscriptor(isSubscriptor.data);
	};

	const onBackClick = (step: number) => {
		if (step === 5) {
			wizard.current?.goBack(1, step);
		}
	};

	const renderFooter = () => {
		return (
			<Footer>
				<Modal
					isVisible={showResume}
					type="full-screen"
					onClose={() => setShowResume(false)}
				>
					<PriceResume
						style={{
							width: "unset",
							padding: 0,
							paddingBottom: 48,
						}}
						product={props.product}
						isSubscriptor={isSubscriptor}
						shipping={shipping}
					/>
				</Modal>
				<Text type="h6" weight="medium">
					{new Intl.NumberFormat("es-ES", {
						style: "currency",
						currency: "EUR",
						maximumFractionDigits: 2,
					}).format((price + (shipping?.amount || 0)) * 1.21)}
				</Text>
				<Text
					type="p2"
					style={{ textDecoration: "underline", cursor: "pointer" }}
					onClick={() => setShowResume(true)}
				>
					Ver resumen
				</Text>
			</Footer>
		);
	};

	return (
		<WizardContainer
			ref={wizard}
			onLogin={onLogin}
			onBackClick={onBackClick}
			views={[
				//Send location
				{
					centralView: currentUser && (
						<ShippingLocationStep
							view={props.view}
							title="Indica en qué dirección quieres recibir tu Pack"
							subtitle="Asegúrate de indicar la dirección completa para que llegue correctamente"
							user={currentUser}
							price={price}
							shipping={shipping?.amount || 0}
							Footer={isMobile ? renderFooter() : undefined}
							onShippingChange={(result) => {
								setShipping(result);
							}}
							onFinish={(result) => {
								if (result.error) {
									setShowError({
										show: true,
										message: result.error,
									});
								} else if (result.data) {
									setShippingAddress(result.data);
									wizard.current?.goNext();
								}
							}}
						/>
					),
					rightView: !isMobile ? (
						<PriceResume
							product={props.product}
							isSubscriptor={isSubscriptor}
							shipping={shipping}
						/>
					) : null,
				},
				//Select payment method
				{
					centralView:
						currentUser && shipping && shippingAddress ? (
							<PaymentMethod
								shopProduct={props.product}
								isSubscriptor={isSubscriptor}
								shipping={shipping.amount}
								shippingAddress={shippingAddress}
								user={currentUser}
								view={props.view}
								Footer={isMobile ? renderFooter() : undefined}
								onFinish={(result) => {
									if (result.error) {
										setShowError({
											show: true,
											message: result.error,
										});
									} else if (result.data) {
										wizard.current?.goNext();
									}
								}}
							/>
						) : null,
					rightView: !isMobile ? (
						<PriceResume
							product={props.product}
							isSubscriptor={isSubscriptor}
							shipping={shipping}
						/>
					) : null,
				},
				//Success view
				{
					centralView: currentUser ? (
						<Success
							user={currentUser}
							didSubscribe={"unique"}
							type="pack"
							title={`¡Millones de gracias, ${currentUser.name}!`}
							subtitle="Con la compra de este Pack Solidario de Escritura, estás regalando compañía esta Navidad y durante el año 2026. Esperamos que tu experiencia de escribir una carta en papel a una persona mayor sea única."
							reliefModalProps={{
								hide: true,
							}}
						>
							{isMobile ? (
								<>
									<PackImage
										src={require("../../assets/images/pack/pack.webp")}
									/>
									<SuccessRight />
								</>
							) : (
								<PackImage
									src={require("../../assets/images/pack/pack.webp")}
								/>
							)}
						</Success>
					) : null,
					rightViewStyle: { position: "unset" },
					rightView: !isMobile ? <SuccessRight /> : null,
					hideBackButton: true,
				},
			]}
		/>
	);
};
export default Checkout;
export interface Props {
	product: ProductScheme;
	isSubscriptor: boolean;
	view: string;
}
